// =============================================================================
// app/settings/privacy.tsx — profile visibility / DM permissions (bare-RN port).
//
// MIGRATION (P3 settings): @expo/vector-icons -> components/ui/Icon; the inline
// expo-router <Stack.Screen options> header is dropped (SettingsStack owns it).
//
// Apple-aligned pass: full-bleed ParticleBackground aurora under the native
// stack header, uniform GlassCard surfaces, tokenized typography (bold caption
// section headers, weight-driven hierarchy), leading icon chips on every row.
// All API/business logic is unchanged.
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Switch,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '../../components/ui/Icon';
import { api } from '../../services/api';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';
import { GlassCard } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';

type Visibility = 'public' | 'private';
type DMPermission = 'everyone' | 'friends' | 'none';

interface PrivacySettings {
  profileVisibility: Visibility;
  allowMessages: DMPermission;
  showOnlineStatus: boolean;
}

const DM_OPTIONS: { value: DMPermission; label: string; desc: string }[] = [
  { value: 'everyone', label: 'Everyone', desc: 'Anyone can send you a DM' },
  { value: 'friends', label: 'Friends', desc: 'Only people you are friends with' },
  { value: 'none', label: 'No one', desc: 'No one can DM you' },
];

export default function PrivacyScreen() {
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PrivacySettings>({
    profileVisibility: 'public',
    allowMessages: 'everyone',
    showOnlineStatus: true,
  });

  const fetchPrivacy = useCallback(async () => {
    setLoading(true);
    const res = await api<PrivacySettings>('/settings/privacy');
    if (res.success && res.data) {
      setSettings({
        profileVisibility: res.data.profileVisibility || 'public',
        allowMessages: res.data.allowMessages || 'everyone',
        showOnlineStatus: res.data.showOnlineStatus !== false,
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPrivacy();
  }, [fetchPrivacy]);

  async function saveField(key: string, value: any) {
    setSaving(true);
    const res = await api('/settings/privacy', {
      method: 'PUT',
      body: JSON.stringify({ [key]: value }),
    });
    setSaving(false);

    if (!res.success) {
      Alert.alert('Error', res.error || 'Failed to save setting.');
      fetchPrivacy(); // revert
    }
  }

  // Functional updates so rapid changes across fields never clobber each other
  // with a stale `settings` snapshot; a failed save refetches (true revert).
  function toggleVisibility() {
    const next: Visibility =
      settings.profileVisibility === 'public' ? 'private' : 'public';
    setSettings((s) => ({ ...s, profileVisibility: next }));
    saveField('profileVisibility', next);
  }

  function setDMPermission(value: DMPermission) {
    setSettings((s) => ({ ...s, allowMessages: value }));
    saveField('allowMessages', value);
  }

  function toggleOnlineStatus(value: boolean) {
    setSettings((s) => ({ ...s, showOnlineStatus: value }));
    saveField('showOnlineStatus', value);
  }

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, containerStyle]}
        showsVerticalScrollIndicator={false}
      >
        {/* Profile Visibility */}
        <Text style={styles.sectionTitle}>Profile Visibility</Text>
        <GlassCard>
          <TouchableOpacity
            style={styles.visibilityRow}
            onPress={toggleVisibility}
            activeOpacity={0.6}
            accessibilityRole="button"
            accessibilityLabel={`Toggle profile visibility, currently ${settings.profileVisibility}`}
          >
            <View style={styles.visibilityLeft}>
              <View style={styles.iconWrap}>
                <Ionicons
                  name={
                    settings.profileVisibility === 'public'
                      ? 'globe-outline'
                      : 'lock-closed-outline'
                  }
                  size={20}
                  color={colors.primary}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>
                  {settings.profileVisibility === 'public'
                    ? 'Public Profile'
                    : 'Private Profile'}
                </Text>
                <Text style={styles.desc}>
                  {settings.profileVisibility === 'public'
                    ? 'Anyone can see your profile and posts'
                    : 'Only approved followers can see your posts'}
                </Text>
              </View>
            </View>
            <View
              style={[
                styles.badge,
                settings.profileVisibility === 'private' && styles.badgePrivate,
              ]}
            >
              <Text
                style={[
                  styles.badgeText,
                  settings.profileVisibility === 'private' && styles.badgeTextPrivate,
                ]}
              >
                {settings.profileVisibility === 'public' ? 'Public' : 'Private'}
              </Text>
            </View>
          </TouchableOpacity>
        </GlassCard>

        {/* DM Permissions */}
        <Text style={styles.sectionTitle}>Direct Messages</Text>
        <GlassCard>
          {DM_OPTIONS.map((opt, idx) => (
            <TouchableOpacity
              key={opt.value}
              style={[
                styles.dmRow,
                idx < DM_OPTIONS.length - 1 && styles.dmRowBorder,
              ]}
              onPress={() => setDMPermission(opt.value)}
              activeOpacity={0.6}
              accessibilityRole="button"
              accessibilityLabel={opt.label}
              accessibilityState={{ selected: settings.allowMessages === opt.value }}
            >
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>{opt.label}</Text>
                <Text style={styles.desc}>{opt.desc}</Text>
              </View>
              <View
                style={[
                  styles.radio,
                  settings.allowMessages === opt.value && styles.radioActive,
                ]}
              >
                {settings.allowMessages === opt.value && (
                  <View style={styles.radioInner} />
                )}
              </View>
            </TouchableOpacity>
          ))}
        </GlassCard>

        {/* Online Status */}
        <Text style={styles.sectionTitle}>Activity</Text>
        <GlassCard>
          <View style={styles.toggleRow}>
            <View style={styles.toggleLeft}>
              <View style={[styles.iconWrap, styles.iconWrapSuccess]}>
                <Ionicons name="ellipse" size={12} color={colors.success} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Show Online Status</Text>
                <Text style={styles.desc}>
                  Let others see when you are active
                </Text>
              </View>
            </View>
            <Switch
              value={!!settings.showOnlineStatus}
              onValueChange={toggleOnlineStatus}
              trackColor={{ false: colors.faint, true: colors.primary + '60' }}
              thumbColor={settings.showOnlineStatus ? colors.primary : colors.card}
              accessibilityLabel="Show online status"
            />
          </View>
        </GlassCard>

        {saving && (
          <View style={styles.savingOverlay}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text style={styles.savingText}>Saving...</Text>
          </View>
        )}

        <View style={{ height: spacing.xxxl }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    padding: spacing.lg,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
    marginTop: spacing.lg,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconWrapSuccess: {
    backgroundColor: colors.success + '15',
  },
  visibilityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  visibilityLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    flex: 1,
  },
  badge: {
    backgroundColor: colors.primary + '15',
    borderRadius: radius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
  },
  badgePrivate: {
    backgroundColor: colors.warning + '15',
  },
  badgeText: {
    ...typographyScale.caption,
    color: colors.primary,
    ...fonts.semibold,
  },
  badgeTextPrivate: {
    color: colors.warning,
  },
  label: {
    ...typographyScale.body,
    color: colors.ink,
    ...fonts.medium,
  },
  desc: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 2,
    ...fonts.regular,
  },
  dmRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    gap: spacing.md,
  },
  dmRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
    paddingBottom: spacing.md,
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: colors.faint,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioActive: {
    borderColor: colors.primary,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.primary,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  toggleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    flex: 1,
  },
  savingOverlay: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  savingText: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.medium,
  },
});
